// First-run Help: a "? Help" button (top-right) + a short guide modal explaining the
// review flow, so a client opening it for the first time isn't lost. Auto-opens once.
import { isTouch } from "./mobile";

const SEEN_KEY = "geaar.helpSeen";

// Phone / tablet wording for the steps whose desktop text talks about a mouse or keyboard.
const TOUCH_BODY: Record<string, string> = {
  "1": "Tap <b>☰ Layers</b> (top-left of the map) to open the layer list, then tick a layer's <b>2D</b> or <b>3D</b> box to show it on the map. Tick both to check the 2D plan lines up with the 3D model.",
  "2": "Drag one finger to orbit, pinch to zoom, and drag two fingers to tilt. More view settings, including <b>▦ Plan view</b> and the <b>basemap</b>, are under the <b>☰</b> menu at the top right.",
  "3": "Tap a layer's <b>name</b> to fly to it; the list slides away and the layer pulses so you can spot it. The bar along the bottom then shows that layer's sign-off.",
  "5": "Tools under the project name: <b>📍 Comment</b> (tap the map), <b>✏ Draw</b> (the map holds still while you draw; tap <b>Done</b> to save the strokes as one issue), <b>▰ Area</b> (tap the corners, then <b>Finish</b>), <b>📏 Measure</b>. Pick a layer first so the markup attaches to it.",
};

const STEPS: { n: string; title: string; body: string }[] = [
  {
    n: "1",
    title: "Show the data",
    body: "In the tree on the left, tick a layer's <b>2D</b> or <b>3D</b> box to show it on the map. Tick both to check the 2D plan lines up with the 3D model.",
  },
  {
    n: "2",
    title: "Look around",
    body: "Drag to orbit. Scroll, or pinch on a laptop trackpad, to zoom toward the cursor. Keyboard: <b>W A S D</b> to fly, <b>Q</b> / <b>E</b> down / up, <b>arrow keys</b> to turn and tilt, hold <b>Shift</b> to go faster. Use <b>▦ Plan view</b> for a straight top-down footprint check, and switch the <b>basemap</b> (satellite / terrain) if it helps.",
  },
  {
    n: "3",
    title: "Pick a layer",
    body: "Click a layer's <b>name</b> to fly to it; the layer pulses so you can spot it. Turbine layers have a <b>Red tips</b> button to show the blade markings. The bar along the bottom then shows that layer's sign-off.",
  },
  {
    n: "4",
    title: "Sign it off",
    body: "In the bottom bar, mark <b>2D</b> and <b>3D</b> each: <b>✓ Approve</b>, <b>✗ Needs work</b> (type a note - that's the issue), or <b>⊘ Exclude</b>. A note shows in red under the layer name.",
  },
  {
    n: "5",
    title: "Mark it up",
    body: "Tools above the map: <b>📍 Pin</b> a comment, <b>✏ Draw</b> (pick a colour and thickness, draw as many strokes as you like, then click <b>Done</b> to save them as one issue), <b>▰ Area</b>, <b>📏 Measure</b>. Pick a layer first so the markup attaches to it.",
  },
  {
    n: "6",
    title: "It saves for everyone",
    body: "Your issues, notes and sign-offs save automatically and show up for the whole team - no need to refresh. Use the filter chips at the top of the tree to see only flagged or to-review layers.",
  },
];

export function mountHelp(container?: HTMLElement): void {
  const btn = document.createElement("button");
  // Sits in the toolbar flow (not floating) so it never covers other controls.
  btn.className = container ? "tool-btn help-btn" : "help-btn help-float";
  btn.textContent = "? Help";
  btn.title = "How to use this tool";
  (container ?? document.body).append(btn);

  const overlay = document.createElement("div");
  overlay.className = "help-overlay";
  overlay.innerHTML = `
    <div class="help-modal">
      <div class="help-head">
        <h2>How to use the Review Station</h2>
        <button class="help-close" title="Close">✕</button>
      </div>
      <p class="help-intro">Confirm each delivered layer is correct and sign it off. Everything you flag or approve is saved and shared with the team.</p>
      <ol class="help-steps">
        ${STEPS.map(
          (s) =>
            `<li><span class="help-num">${s.n}</span><div><div class="help-step-title">${s.title}</div><div class="help-step-body">${(isTouch && TOUCH_BODY[s.n]) || s.body}</div></div></li>`,
        ).join("")}
      </ol>
      <div class="help-foot"><button class="tool-btn help-got">Got it</button></div>
    </div>`;
  document.body.append(overlay);

  const open = () => overlay.classList.add("show");
  const close = () => {
    overlay.classList.remove("show");
    try {
      localStorage.setItem(SEEN_KEY, "1");
    } catch {
      /* private mode - fine, it'll just open again next time */
    }
  };

  btn.addEventListener("click", open);
  overlay.querySelector(".help-close")!.addEventListener("click", close);
  overlay.querySelector(".help-got")!.addEventListener("click", close);
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) close();
  });

  // First-timers: open it automatically once.
  let seen = false;
  try {
    seen = localStorage.getItem(SEEN_KEY) === "1";
  } catch {
    /* ignore */
  }
  if (!seen) window.setTimeout(open, 600);
}
